<template>
    <div
        id="nlf-agent-side-panel"
        ref="panel"
        class="right-panel"
        :class="{ hide: !show }"
        :style="panelStyle"
        data-test="nlf-agent-side-panel"
    >
        <section class="ai-panel" aria-labelledby="nlf-agent-panel-title">
            <div class="ai-panel-header">
                <div class="ai-powered-panel">
                    <i class="far fa-sparkles" aria-hidden="true"></i>
                    <span class="ai-panel-powered-text">AI POWERED</span>
                </div>
                <h2 id="nlf-agent-panel-title" class="ai-panel-title">
                    What do you want to filter or group by?
                </h2>
                <p class="ai-panel-intro">
                    I can help find available data and apply filters, and group by settings. Enter your request in the chat.
                </p>
            </div>

            <div
                ref="chatMessages"
                class="ai-panel-messages"
                role="region"
                aria-label="AI assistant conversation"
                aria-live="polite"
                aria-relevant="additions text"
                data-test="nlf-agent-messages"
            >

                <div
                    v-for="(msg, vIdx) in visibleMessages"
                    :key="msg._stableId || `vm-${vIdx}`"
                    class="chat-message"
                    :class="[msg.role, msg.msgType ? `msg-${msg.msgType}` : '']"
                >
                    <div class="message-label">
                        <i v-if="msg.role === 'user'" class="fas fa-user" aria-hidden="true"></i>
                        <i v-if="msg.role === 'assistant'" class="far fa-robot" aria-hidden="true"></i>
                        <span>{{ msg.role === 'user' ? 'Me' : 'AI Agent' }}</span>
                    </div>
                    <div class="message-bubble" :class="msg.msgType ? `type-${msg.msgType}` : ''">
                        <div
                            v-if="msg.content"
                            class="message-content"
                            @click="handleContentClick($event)"
                            @keydown="handleContentKeydown($event)"
                        >
                            <span v-safe-html="formatMessageContent(msg.content)"></span>
                        </div>
                        <div v-if="msg.options && msg.options.length" class="options-container">
                            <button
                                v-for="(option, optIdx) in msg.options"
                                :key="optIdx"
                                type="button"
                                class="option-chip"
                                :class="{ 'selected-option': isOptionSelected(option) }"
                                :disabled="isThinking || isStreaming"
                                :aria-label="optionChipAriaLabel(option)"
                                :data-test="`nlf-agent-option-chip-${optIdx}`"
                                @click="selectOption(option)"
                                @keydown.enter.prevent="selectOption(option)"
                                @keydown.space.prevent="selectOption(option)"
                            >
                                <i v-if="isOptionSelected(option)" class="fas fa-check" aria-hidden="true"></i>
                                {{ optionChipDisplay(option) }}
                            </button>
                        </div>
                    </div>
                    <div v-if="msg.role === 'assistant' && msg.queryId" class="feedback-container">
                        <div
                            v-if="messageFeedback[msg._stableId] && messageFeedback[msg._stableId].showThankYou"
                            class="feedback-thank-you"
                            :class="{ 'feedback-error': messageFeedback[msg._stableId] && messageFeedback[msg._stableId].isError }"
                        >
                            {{ (messageFeedback[msg._stableId] && messageFeedback[msg._stableId].message) || 'Thank you for your feedback!' }}
                        </div>
                        <div v-else class="feedback-icons">
                            <button
                                type="button"
                                class="feedback-icon-btn"
                                :class="{ highlighted: messageFeedback[msg._stableId] && messageFeedback[msg._stableId].type === 'positive' }"
                                data-test="nlf-agent-feedback-thumbs-up-btn"
                                aria-label="Thumbs up"
                                @click="handleThumbsUp(msg._stableId)"
                            >
                                <i :class="messageFeedback[msg._stableId] && messageFeedback[msg._stableId].type === 'positive' ? 'fas fa-thumbs-up' : 'far fa-thumbs-up'"></i>
                            </button>
                            <button
                                type="button"
                                class="feedback-icon-btn"
                                :class="{ highlighted: messageFeedback[msg._stableId] && messageFeedback[msg._stableId].type === 'negative' }"
                                data-test="nlf-agent-feedback-thumbs-down-btn"
                                aria-label="Thumbs down"
                                @click="handleThumbsDown(msg._stableId)"
                            >
                                <i :class="messageFeedback[msg._stableId] && messageFeedback[msg._stableId].type === 'negative' ? 'fas fa-thumbs-down' : 'far fa-thumbs-down'"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="isThinking || isStreaming" class="chat-message assistant">
                    <div class="message-bubble thinking" aria-live="polite">
                        <div class="thinking-dots" aria-hidden="true">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        <span class="sr-only">Assistant is responding</span>
                    </div>
                </div>
            </div>

            <div class="ai-panel-input">
                <div class="ai-panel-input-toolbar">
                    <p class="ai-disclaimer" data-test="nlf-agent-ai-disclaimer">
                        Responses are AI-generated. Please review for accuracy.
                    </p>
                    <v-btn
                        type="button"
                        icon
                        small
                        text
                        aria-label="Clear AI assistant conversation"
                        data-test="nlf-agent-clear-btn"
                        class="clear-btn-above"
                        @click="$emit('reset-chat')"
                    >
                        <i class="fas fa-redo-alt" aria-hidden="true"></i>
                    </v-btn>
                </div>
                <div class="chat-input-gradient-wrap">
                    <v-text-field
                        id="nlf-agent-chat-input"
                        v-model="localChatInput"
                        placeholder="Type a message..."
                        dense
                        outlined
                        hide-details
                        @keydown.enter.exact.prevent="sendMessage"
                        :disabled="isThinking"
                        class="chat-input"
                        data-test="nlf-agent-chat-input"
                    />
                </div>
                <div class="input-buttons">
                    <v-btn
                        depressed
                        :disabled="!localChatInput.trim() || isThinking"
                        aria-label="Send message"
                        data-test="nlf-agent-enter-btn"
                        @click="sendMessage"
                        small
                        class="enter-btn"
                    >
                        <i class="far fa-sparkles enter-btn-sparkle" aria-hidden="true"></i>
                        Enter
                    </v-btn>
                </div>
            </div>
        </section>

        <FeedbackModal
            v-model="showFeedbackModal"
            @submit="handleFeedbackSubmit"
            @cancel="handleFeedbackCancel"
        />
    </div>
</template>

<script>
import { mapLocalState } from '@/util/vuexHelper'
import { isDemographicOptionApplied, isCourseOptionApplied, isCourseOptionComplete, isSearchOptionSelected } from '../NlfAgentProcessor'
import FeedbackModal from './FeedbackModal.vue'

export default {
    name: 'RightSidePanel',

    components: {
        FeedbackModal,
    },

    inject: ['localStore'],

    props: {
        show: { type: Boolean, default: false },
    },

    data() {
        return {
            localChatInput: '',
            selectedOptions: [],
            messageFeedback: {},
            showFeedbackModal: false,
            currentFeedbackMessageId: null,
        }
    },

    computed: {
        ...mapLocalState(['chatMessages', 'isThinking', 'isStreaming', 'streamingMessageIndex']),
        visibleMessages() {
            return this.chatMessages.filter(msg => {
                if (msg.role === 'user') return true
                if (msg.msgType === 'thinking' || msg.msgType === 'tool') return false
                return Boolean(msg.content) || (msg.options && msg.options.length)
            })
        },
        panelStyle() {
            if (!this.show) return {}
            return {
                width: '22vw',
                minWidth: '22vw',
                maxWidth: '22vw',
            }
        },
    },

    watch: {
        chatMessages: {
            handler() {
                this.scrollToBottom()
            },
            deep: true
        },
    },

    methods: {
        optionChipDisplay(option) {
            if (typeof option !== 'object' || option === null) {
                return String(option)
            }

            // Handle filter entry format (with type field)
            if (option.type) {
                switch (option.type) {
                    case 'gender':
                        return this.displayDemographicOption(option, 'gender')
                    case 'ethnicity':
                        return this.displayDemographicOption(option, 'ethnicity')
                    case 'disability':
                    case 'meal_status':
                    case 'tag': {
                        const displayName = option.params?.display_name
                        return displayName ? (Array.isArray(displayName) ? displayName.join(', ') : displayName) : ''
                    }
                    case 'attendance':
                        return option.params?.display_name ?? ''
                    case 'group_by':
                        return option.params?.value ?? ''
                    case 'incident':
                        return this.displayIncidentOption(option)
                    case 'assessment':
                        return this.displayAssessmentOption(option)
                    case 'demographic':
                        return this.displayCustomDemographicOption(option)
                    case 'course': {
                        const courses = (option.params?.course_name || []).join(', ')
                        const teacher = option.params?.user_name
                        const label = courses && teacher ? `${courses} (${teacher})` : (courses || teacher || '')
                        // One button per period carries identical courses, so the period has to show
                        const periods = (option.params?.period || []).join(', ')
                        return periods ? `${periods}: ${label}` : label
                    }
                    default:
                        console.error('Unknown option type:', option.type, JSON.stringify(option, null, 2))
                        return ''
                }
            }

            // Handle other formats (schools/grades)
            if (option.id !== undefined && option.name !== undefined) {
                return option.name
            }
            if (option.label) {
                return String(option.label)
            }

            console.error('Unknown option format, cannot display:', JSON.stringify(option, null, 2))
            return ''
        },

        /**
         * Display demographic option with fallback to specific field.
         * @param {Object} option - Option object with params
         * @param {string} valueKey - The specific field key to use as fallback (e.g., 'gender', 'ethnicity')
         * @returns {string} Formatted display string
         */
        displayDemographicOption(option, valueKey) {
            if (!option.params) return ''

            const { display_name, [valueKey]: value } = option.params

            switch (true) {
                case Boolean(display_name):
                    return Array.isArray(display_name) ? display_name.join(', ') : display_name
                case Boolean(value):
                    return Array.isArray(value) ? value.join(', ') : value
                default:
                    return ''
            }
        },

        /**
         * Button label for a custom demographic option, built from its structured params rather
         * than an agent-supplied string. Formats match the manual panel / student profile:
         * "Name: Yes", "Name: Value1, Value2", "Name Contains Value", "Name Between 3 and 5".
         */
        displayCustomDemographicOption(option) {
            const params = option.params
            if (!params) return ''

            const demographic = (this.$store.getters['global/demographicsAll'] || [])
                .find(d => d.id === params.demographic_id)
            const name = params.name || demographic?.name || ''
            const valueType = params.value_type || demographic?.value_type

            if (Array.isArray(params.option_id) && params.option_id.length) {
                const names = Array.isArray(params.display_name) ? params.display_name : []
                const excludeFlags = params.exclude || []
                const labels = params.option_id.map((id, idx) => {
                    const known = demographic?.options?.find(o => o.id === id)
                    const label = names[idx] || known?.display_name || known?.value || `${id}`
                    return excludeFlags[idx] ? `exclude ${label}` : label
                })
                return `${name}: ${labels.join(', ')}`
            }

            // Only name a value the option actually carries; a field-only option (no value yet)
            // falls through to just the field name below.
            if (valueType === 'boolean' && ['0', '1'].includes(String(params.value))) {
                return `${name}: ${String(params.value) === '1' ? 'Yes' : 'No'}`
            }

            if (params.operator && params.value != null) {
                const operatorText = this.$demographicsOperators().find(o => o.code === params.operator)?.text || params.operator
                const valueText = params.operator === 'bt' ? `${params.value} and ${params.value2}` : params.value
                return `${name} ${operatorText} ${valueText}`
            }

            return name
        },

        displayAssessmentOption(option) {
            if (!option.params) return ''
            const display = [
                option.params.data_point_type?.name,
                option.params.sub_category?.display_name || option.params.sub_category?.name,
                option.params.data_point_name?.alias || option.params.data_point_name?.name
            ].filter(Boolean).join(' \u2014 ')

            // Append performance band and score detail to option button labels so it doesn't
            // look like different buttons represent the same filter.
            const details = [
                option.params.score_detail_type?.description || option.params.score_detail_type?.code,
                option.params.target_set?.name,
                option.params.target_descriptor?.map(d => d.name).join('/')
            ].filter(Boolean).join(', ')

            return details ? `${display} (${details})` : display
        },

        displayIncidentOption(option) {
            if (!option.params) return ''

            // If agent provided a display_name (for count-based or other options), use that
            if (option.params.display_name) {
                return option.params.display_name
            }

            // Otherwise, build from multiselect field names
            const names = [
                ...(option.params.incident_behavior_type_name || []),
                ...(option.params.incident_behavior_name || []),
                ...(option.params.incident_location_name || []),
                ...(option.params.incident_response_name || [])
            ]
            return names.join(', ') || 'Incident'
        },

        optionChipAriaLabel(option) {
            const display = this.optionChipDisplay(option)
            const isSelected = this.isOptionSelected(option)

            // A teacher-only course button asks the agent a follow-up; it doesn't add or remove a filter
            if (option.type === 'course' && !isCourseOptionComplete(option.params)) {
                return `Choose option: ${display}`
            }

            if (option.kind === 'school' || option.kind === 'grade') {
                return isSelected ? `Remove filter: ${display}` : `Add filter: ${display}`
            }

            // School year is single-select, so a selected option can't be toggled off
            if (option.kind === 'school_year') {
                return isSelected ? `Selected school year: ${display}` : `Select school year: ${display}`
            }

            // For filter options, indicate toggle behavior
            if (option.type && ['gender', 'ethnicity', 'disability', 'meal_status', 'tag', 'attendance', 'incident', 'assessment', 'demographic', 'course'].includes(option.type)) {
                return isSelected ? `Remove filter: ${display}` : `Add filter: ${display}`
            }

            return `Choose option: ${display}`
        },

        isOptionSelected(option) {
            // Check if this option has been selected
            if (typeof option !== 'object' || !option) return false

            // Assessment filters are additive — multiple can coexist (different assessments,
            // windows, or performance bands), so check ALL of them for a match instead of just
            // the first one, unlike the other filter types below which only ever have one filter.
            if (option.type === 'assessment' && option.params) {
                const assessmentFilters = this.$parent?.params?.filters?.filter(f => f.type === 'assessment') || []
                return assessmentFilters.some(appliedFilter => this.matchesAssessmentOption(appliedFilter, option))
            }

            // Applied demographic filters are typed `demo_<id>`, so the generic same-type lookup
            // below would never find them.
            if (option.type === 'demographic' && option.params) {
                return isDemographicOptionApplied(this.$parent?.params?.filters, option)
            }

            if (option.type === 'course' && option.params) {
                const filters = this.$parent?.params?.filters
                // A teacher-only button has no courses to compare, so it reads as selected once that
                // teacher's course filter is applied.
                if (!isCourseOptionComplete(option.params)) {
                    return (filters || []).some(f => f.type === 'course' && Number(f.params?.user_id) === Number(option.params.user_id))
                }
                return isCourseOptionApplied(filters, option)
            }

            // For filter options (gender, ethnicity, disability), check if the value is in the applied filter
            if (option.type && option.params) {
                const filterType = option.type
                const appliedFilter = this.$parent?.params?.filters?.find(f => f.type === filterType)

                if (!appliedFilter) return false

                // Check based on filter type
                switch (filterType) {
                    case 'gender': {
                        const optionGenders = option.params.gender || []
                        const appliedGenders = appliedFilter.params?.gender || []
                        return optionGenders.every(val => appliedGenders.includes(val))
                    }
                    case 'ethnicity': {
                        const optionEthnicities = option.params.ethnicity || []
                        const appliedEthnicities = appliedFilter.params?.ethnicity || []
                        return optionEthnicities.every(val => appliedEthnicities.includes(val))
                    }
                    case 'disability': {
                        const optionDisabilityIds = option.params.student_disability_type_id || []
                        const appliedDisabilityIds = appliedFilter.params?.student_disability_type_id || []
                        return optionDisabilityIds.every(val => appliedDisabilityIds.includes(val))
                    }
                    case 'meal_status': {
                        const optionMealStatusIds = option.params.meal_status_id || []
                        const appliedMealStatusIds = appliedFilter.params?.meal_status_id || []
                        return optionMealStatusIds.every(val => appliedMealStatusIds.includes(val))
                    }
                    case 'tag': {
                        const optionTagIds = option.params.tag_id || []
                        const appliedTagIds = appliedFilter.params?.tag_id || []
                        return optionTagIds.every(val => appliedTagIds.includes(val))
                    }
                    case 'attendance': {
                        const optionMonths = option.params?.occurrence_month_num || []
                        const optionWeekdays = option.params?.occurrence_weekday_num || []

                        // Check month/weekday refinements (mutually exclusive)
                        if (optionMonths.length > 0) {
                            const appliedMonths = appliedFilter.params?.occurrence_month_num || []
                            return optionMonths.every(m => appliedMonths.includes(m))
                        } else if (optionWeekdays.length > 0) {
                            const appliedWeekdays = appliedFilter.params?.occurrence_weekday_num || []
                            return optionWeekdays.every(w => appliedWeekdays.includes(w))
                        }

                        // Check for selection buttons (rate_type or filter_by with default range 0-100)
                        const isDefaultRange = option.params?.attendance_value_min === 0 &&
                                              option.params?.attendance_value_max === 100

                        if (isDefaultRange) {
                            // Rate type selection button
                            if (option.params?.rate_type && !option.params?.filter_by) {
                                return appliedFilter.params?.rate_type === option.params?.rate_type
                            }
                            // Filter by selection button
                            if (option.params?.filter_by && !option.params?.rate_type) {
                                return appliedFilter.params?.filter_by === option.params?.filter_by
                            }
                        }

                        // Check complete attendance filter (specific min/max range)
                        if (option.params?.attendance_value_min !== undefined || option.params?.attendance_value_max !== undefined) {
                            return appliedFilter.params?.attendance_value_min === option.params?.attendance_value_min &&
                                   appliedFilter.params?.attendance_value_max === option.params?.attendance_value_max
                        }

                        return false
                    }
                    case 'incident': {
                        // Check if any of the incident multiselect fields match
                        // An option is selected if ALL its values are in the applied filter
                        const optionTypeIds = option.params?.incident_behavior_type_id || []
                        const optionBehaviorIds = option.params?.incident_behavior_id || []
                        const optionLocationIds = option.params?.incident_location_id || []
                        const optionResponseIds = option.params?.incident_response_id || []

                        const appliedTypeIds = appliedFilter.params?.incident_behavior_type_id || []
                        const appliedBehaviorIds = appliedFilter.params?.incident_behavior_id || []
                        const appliedLocationIds = appliedFilter.params?.incident_location_id || []
                        const appliedResponseIds = appliedFilter.params?.incident_response_id || []

                        // Check which field this option belongs to and verify all values are present
                        if (optionTypeIds.length > 0) {
                            return optionTypeIds.every(id => appliedTypeIds.includes(id))
                        }
                        if (optionBehaviorIds.length > 0) {
                            return optionBehaviorIds.every(id => appliedBehaviorIds.includes(id))
                        }
                        if (optionLocationIds.length > 0) {
                            return optionLocationIds.every(id => appliedLocationIds.includes(id))
                        }
                        if (optionResponseIds.length > 0) {
                            return optionResponseIds.every(id => appliedResponseIds.includes(id))
                        }

                        return false
                    }
                }
            }

            // School, grade and school year options are selected when they are in the search bar's selection
            if (option.kind === 'school' || option.kind === 'grade' || option.kind === 'school_year') {
                return isSearchOptionSelected(this.$parent?.$refs?.demographicSearch, option)
            }

            // For any other option, check if it was clicked (old behavior)
            return this.selectedOptions.some(selected => {
                return JSON.stringify(selected) === JSON.stringify(option)
            })
        },

        /**
         * Check whether an assessment option matches a specific applied assessment filter.
         * @param {Object} appliedFilter - One applied filter of type 'assessment'
         * @param {Object} option - Assessment option with {params: {...}}
         * @returns {boolean} True if this option is what produced appliedFilter
         */
        matchesAssessmentOption(appliedFilter, option) {
            // Match by ID or name — Agent sometimes sends placeholder id which won't match
            // validated id even though the name is correct.
            const fieldMatches = (filterId, paramsId, filterName, paramsName) => {
                if (filterId != null && paramsId != null && filterId === paramsId) return true
                if (filterName && paramsName && filterName === paramsName) return true
                return false
            }

            const matchesCore = fieldMatches(
                appliedFilter.params?.data_point_type_id, option.params?.data_point_type?.id,
                appliedFilter.editorParams?.data_point_type?.name, option.params?.data_point_type?.name
            ) && fieldMatches(
                appliedFilter.params?.sub_category_id, option.params?.sub_category?.id,
                appliedFilter.editorParams?.sub_category?.name || appliedFilter.editorParams?.sub_category?.display_name,
                option.params?.sub_category?.display_name || option.params?.sub_category?.name
            ) && fieldMatches(
                appliedFilter.params?.data_point_name_id, option.params?.data_point_name?.id,
                appliedFilter.editorParams?.data_point_name?.name, option.params?.data_point_name?.name
            )
            if (!matchesCore) return false

            const optionScoreDetailTypeId = option.params?.score_detail_type?.id
            if (optionScoreDetailTypeId !== undefined && appliedFilter.params?.score_detail_type_id !== optionScoreDetailTypeId) return false

            const optionTargetSet = option.params?.target_set
            if (optionTargetSet && !fieldMatches(
                appliedFilter.params?.target_set_id, optionTargetSet.id,
                appliedFilter.editorParams?.target_set?.name, optionTargetSet.name
            )) return false

            const optionDescriptors = option.params?.target_descriptor || []
            if (optionDescriptors.length) {
                const appliedDescriptorIds = appliedFilter.params?.target_descriptor_id || []
                const appliedDescriptorNames = (appliedFilter.editorParams?.target_descriptor || []).map(d => d.name)
                return optionDescriptors.every(d =>
                    (d.id != null && appliedDescriptorIds.includes(d.id)) ||
                    (d.name && appliedDescriptorNames.includes(d.name))
                )
            }

            return true
        },

        sendMessage() {
            const content = this.localChatInput.trim()
            if (!content) return
            this.localChatInput = ''
            this.$emit('send-message', content)
        },

        selectOption(option) {
            // Track options for all kinds of ambiguities
            if (typeof option === 'object' && option !== null && !this.isOptionSelected(option)) {
                this.selectedOptions.push(JSON.parse(JSON.stringify(option)))
            }

            this.$emit('select-option', option)
        },

        handleContentClick(event) {
            this.activateInlineOption(event.target, event)
        },

        handleContentKeydown(event) {
            if (event.key !== 'Enter' && event.key !== ' ') return
            if (this.activateInlineOption(event.target, event)) {
                event.preventDefault()
            }
        },

        activateInlineOption(target, event) {
            if (this.isThinking || this.isStreaming) return false
            const el = (target && target.classList && target.classList.contains('inline-option'))
                ? target
                : (target && typeof target.closest === 'function' ? target.closest('.inline-option') : null)
            if (!el || !el.classList.contains('inline-option')) return false
            const option = el.dataset.option
            if (!option) return false
            this.selectOption(option)
            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault()
            }
            return true
        },

        scrollToBottom() {
            this.$nextTick(() => {
                if (this.$refs.chatMessages) {
                    this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
                }
            })
        },

        formatMessageContent(content) {
            if (!content) return ''

            let formatted = content
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')

            formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>')

            formatted = formatted.replace(
                /(?:(?:are|options|windows|choices):\s*)([A-Za-z0-9][A-Za-z0-9\s]*(?:,\s*[A-Za-z0-9][A-Za-z0-9\s]*){2,})/gi,
                (match, itemsPart) => {
                    const prefix = match.slice(0, match.indexOf(itemsPart))
                    const items = itemsPart.split(/,\s*/)
                    if (items.every(item => item.trim().length <= 25 && item.trim().length > 0)) {
                        return prefix + items
                            .map(item => {
                                const text = item.trim()
                                const dataOption = text.replace(/"/g, '&quot;')
                                const ariaLabel = text
                                    .replace(/&/g, '&amp;')
                                    .replace(/"/g, '&quot;')
                                    .replace(/</g, '&lt;')
                                    .replace(/>/g, '&gt;')
                                return `<span class="inline-option" role="button" tabindex="0" data-option="${dataOption}" aria-label="Select ${ariaLabel}">${text}</span>`
                            })
                            .join(', ')
                    }
                    return match
                }
            )

            formatted = formatted.replace(/\n/g, '<br>')
            return formatted
        },

        async handleThumbsUp(messageId) {
            // Guard against duplicate submissions - check if already submitted or in progress
            if (this.messageFeedback[messageId]?.showThankYou ||
                this.messageFeedback[messageId]?.submitting) return

            const message = this.visibleMessages.find(msg => msg._stableId === messageId)
            if (!message?.queryId) {
                console.error('Message or queryId not found for feedback')
                return
            }

            this.$set(this.messageFeedback, messageId, {
                type: 'positive',
                highlighted: true,
                showThankYou: false,
                submitting: true,
            })

            try {
                await this.submitFeedback(message.queryId, 1, '')

                setTimeout(() => {
                    this.$set(this.messageFeedback, messageId, {
                        type: 'positive',
                        highlighted: false,
                        showThankYou: true,
                        submitting: false,
                        message: 'Thank you for your feedback!',
                    })
                }, 2000)
            } catch (error) {
                console.error('Error submitting feedback:', error)
                setTimeout(() => {
                    this.$set(this.messageFeedback, messageId, {
                        type: 'positive',
                        highlighted: false,
                        showThankYou: true,
                        submitting: false,
                        message: 'There was an error submitting your feedback',
                        isError: true,
                    })
                }, 2000)
            }
        },

        handleThumbsDown(messageId) {
            // Guard against duplicate submissions
            if (this.messageFeedback[messageId]?.showThankYou ||
                this.messageFeedback[messageId]?.submitting) return

            this.$set(this.messageFeedback, messageId, {
                type: 'negative',
                highlighted: true,
                showThankYou: false,
                submitting: true,
            })

            this.currentFeedbackMessageId = messageId
            this.showFeedbackModal = true
        },

        async handleFeedbackSubmit(feedback) {
            const messageId = this.currentFeedbackMessageId
            const message = this.visibleMessages.find(msg => msg._stableId === messageId)

            if (!message?.queryId) {
                console.error('Message or queryId not found for feedback')
                this.currentFeedbackMessageId = null
                return
            }

            const rating = feedback.type === 'positive' ? 1 : 0

            try {
                await this.submitFeedback(message.queryId, rating, feedback.text)

                this.$set(this.messageFeedback, messageId, {
                    type: feedback.type,
                    text: feedback.text,
                    highlighted: false,
                    showThankYou: true,
                    submitting: false,
                    message: 'Thank you for your feedback!',
                })
            } catch (error) {
                console.error('Error submitting feedback:', error)
                this.$set(this.messageFeedback, messageId, {
                    type: feedback.type,
                    text: feedback.text,
                    highlighted: false,
                    showThankYou: true,
                    submitting: false,
                    message: 'There was an error submitting your feedback',
                    isError: true,
                })
            }

            this.currentFeedbackMessageId = null
        },

        handleFeedbackCancel() {
            const messageId = this.currentFeedbackMessageId

            // Remove the highlighted state - return to default (no feedback given)
            this.$delete(this.messageFeedback, messageId)

            this.currentFeedbackMessageId = null
        },

        async submitFeedback(queryId, rating, feedbackText) {
            if (!queryId) {
                throw new Error('queryId is required for feedback submission')
            }

            const payload = {
                query_id: queryId,
                rating: rating,
                feedback_text: feedbackText,
            }

            const url = this.$models.getUrl('nlfAgentRating', 'create')
            const response = await this.$axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            return response.data
        },
    },
}
</script>

<style lang="scss" scoped>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.right-panel {
    position: relative;
    background-color: white;
    overflow: hidden;
    border-top: 1px solid #B3B7C5;
    border-left: 1px solid #B3B7C5;
    flex-shrink: 0;
    min-height: 0;
    align-self: stretch;
    transition: width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out;
}

.hide {
    width: 0 !important;
    min-width: 0 !important;
    max-width: 0 !important;
    border: none;
}

.ai-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    background: linear-gradient(180deg, #f0fbff 0%, #d7f4fd 100%);
}

.ai-panel-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 14px;
    gap: 4px;
    flex-shrink: 0;
    margin-left: 10px;

    .ai-powered-panel {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        line-height: 1.8;
        color: #747f9e;
        font-weight: 600;
        margin-top: 4px;
    }

    .ai-panel-title {
        margin-bottom: 10px;
        font-size: 18px;
        font-weight: 600;
        color: #2b3963;
        line-height: 1.35;
    }

    .ai-panel-intro {
        margin: 0;
        font-size: 14px;
        line-height: 1.4;
        color: #0a1741;
    }
}

.ai-panel-messages {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
}

.ai-panel-empty {
    p {
        margin: 0;
        font-size: 12px;
        line-height: 1.4;
        color: #666;
    }
}

.ai-panel-input {
    display: flex;
    flex-direction: column;
    padding: 12px;
    flex-shrink: 0;

    .ai-panel-input-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 6px;
    }

    .ai-disclaimer {
        margin: 0;
        margin-left: 4px;
        font-size: 12px;
        line-height: 1.4;
        color: #146EB3;
    }

    .clear-btn-above {
        width: 32px !important;
        min-width: 32px !important;
        height: 32px !important;
        margin: 0 !important;
        color: #146EB3 !important;

        i {
            font-size: 14px;
            color: inherit;
        }
    }

    .chat-input-gradient-wrap {
        width: 100%;
        padding: 2px;
        border-radius: 6px;
        background: linear-gradient(90deg, #76b8dc 0%, #347497 100%);
        box-shadow: 0 0 4px #146eb3;
    }

    .chat-input {
        width: 100%;

        ::v-deep .v-input__slot {
            background: #fff !important;
            border-radius: 4px !important;
        }

        ::v-deep fieldset {
            border-color: transparent !important;
        }
    }

    .input-buttons {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 8px;

        .enter-btn {
            flex: 1;
            width: auto;
            height: 34px;
            min-height: 34px;
            margin: 0;
            background: linear-gradient(90deg, #0679a0 0%, #0b3b56 100%);
            box-shadow: none;

            ::v-deep .v-btn__content {
                color: #fff;
                font-weight: 400;
            }

            .enter-btn-sparkle {
                margin-right: 6px;
                font-size: 14px;
                color: #fff;
            }

            &:not(.v-btn--disabled):hover {
                filter: brightness(1.06);
            }

            &:focus-visible {
                outline: 2px solid #0679a0;
                outline-offset: 2px;
            }

            &.v-btn--disabled {
                opacity: 0.5;
            }
        }
    }
}

.chat-message {
    display: flex;
    flex-direction: column;
    max-width: 95%;

    &.user {
        align-self: flex-end;

        .message-label {
            align-self: flex-end;
            margin-bottom: 4px;
            color: #2b3963;
            margin-right: 14px;

            i {
                color: #2b3963;
            }
        }

        .message-bubble {
            background: #fff;
            color: #0a1741;
            border: 1px solid #2b3963;
            /* TL, TR, BR (sharp — user / right side), BL */
            border-radius: 18px 18px 0 18px;
        }
    }

    &.assistant {
        align-self: flex-start;

        .message-label {
            align-self: flex-start;
            margin-bottom: 4px;
            color: #2b3963;
            margin-left: 12px;

            i {
                color: #2b3963;
            }
        }

        .message-bubble {
            background: #fff;
            color: #0a1741;
            border: 1px solid #2b3963;
            /* TL, TR, BR, BL (sharp — assistant / left side) */
            border-radius: 18px 18px 18px 0;
        }
    }
}

.message-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;

    i {
        font-size: 12px;
    }
}

.message-bubble {
    padding: 8px 12px;
    font-size: 12px;
    line-height: 1.4;

    &.thinking {
        padding: 12px;
    }

}

.message-content {
    word-break: break-word;

    ::v-deep .inline-option:focus {
        outline: none;
    }

    ::v-deep .inline-option:focus-visible {
        outline: 2px solid #1976d2;
        outline-offset: 2px;
        border-radius: 2px;
    }
}

.thinking-dots {
    display: flex;
    gap: 4px;

    span {
        width: 6px;
        height: 6px;
        background: #888;
        border-radius: 50%;
        animation: thinking-bounce 1.4s infinite ease-in-out both;

        &:nth-child(1) { animation-delay: -0.32s; }
        &:nth-child(2) { animation-delay: -0.16s; }
    }
}

@keyframes thinking-bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
}

.streaming-cursor {
    animation: blink 1s infinite;
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}

.options-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
}

.option-chip {
    padding: 6px 10px;
    background: #fff;
    border: 1px solid #347497;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    color: #347497;
    font-weight: 500;
    transition: all 0.15s ease;
    width: 100%;
    text-align: left;
    font-family: inherit;
    line-height: 1.4;

    &:hover:not(:disabled) {
        background: #347497;
        color: #fff;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &.selected-option {
        background: #347497;
        border-color: #347497;
        color: #fff;

        i {
            margin-right: 4px;
            color: #fff;
        }

        &:disabled {
            opacity: 1;
        }
    }
}

.inline-option {
    color: #347497;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: #1565C0;
    }
}

.feedback-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 6px;
    margin-right: 12px;
}

.feedback-icons {
    display: flex;
    gap: 8px;
    align-items: center;
}

.feedback-icon-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #146EB3;
    font-size: 16px;

    i {
        color: inherit;
        transition: color 0.2s ease;
    }

    &:hover {
        transform: scale(1.1);
    }

    &.highlighted {
        color: #146EB3;
        animation: pulse 0.3s ease;
    }
}

@keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
}

.feedback-thank-you {
    font-size: 11px;
    color: #146EB3;
    font-style: italic;

    &.feedback-error {
        color: #d32f2f;
    }
}
</style>
